import React, { useState, useEffect } from 'react';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../ui/accordion';
import ObjectTypeFilterControl from './FilterControls/ObjectTypeFilterControl';
import DateRangeFilterControl from './FilterControls/DateRangeFilterControl';
import DateRangePresets from '../../features/filter/TabFilterModal/DateRangePresets';
import EventAttributeFilterControl from './FilterControls/EventAttributeFilterControl';
import EdgeDurationFilterControl from './FilterControls/EdgeDurationFilterControl';
import EdgeContainmentFilterControl from './FilterControls/EdgeContainmentFilterControl';
import ActivityNodeFilterControl from './FilterControls/ActivityNodeFilterControl';
import ObjectAttributeFilterControl from './FilterControls/ObjectAttributeFilterControl';

/**
 * LocalFilterPanel - Container component that orchestrates all filter controls
 *
 * Provides an accordion-style UI for managing local filters.
 * Each filter type can be expanded/collapsed independently.
 *
 * @param {object} localFilters - Current local filter state
 * @param {Function} updateLocalFilter - Callback to update a specific filter
 * @param {string} activeFile - Current active file
 * @param {Array} enabledFilterTypes - Which filter types to show (defaults to all)
 * @param {boolean} disabled - Whether all controls are disabled
 * @param {boolean} dateRangeShowTime - Whether the Date Range section also shows time-of-day inputs (default false)
 */
const EMPTY_FILTER_VALUES = {
  objectTypes: [],
  dateRange: { startDate: null, endDate: null, enabled: false },
  eventAttributeFilters: [],
  edgeDurationFilters: [],
  edgeContainmentFilters: { enabled: false, edges: [], matchType: 'any', mode: 'include' },
  activityNodeFilters: { enabled: false, nodes: [], matchType: 'any', mode: 'include' },
  objectAttributeFilters: [],
};

const FILTER_TYPE_TO_KEY = {
  objectTypes: 'objectTypes',
  dateRange: 'dateRange',
  eventAttributes: 'eventAttributeFilters',
  edgeDuration: 'edgeDurationFilters',
  edgeContainment: 'edgeContainmentFilters',
  activityNodes: 'activityNodeFilters',
  objectAttributes: 'objectAttributeFilters',
};

const LocalFilterPanel = ({
  localFilters,
  updateLocalFilter,
  activeFile,
  enabledFilterTypes = null, // null means show all
  disabled = false,
  onAddEdgeDuration,
  onAddEdgeContainment,
  dateRangeShowTime = false,
}) => {
  // Track which sections are expanded — pre-open any section that already has active filters
  const [expandedSections, setExpandedSections] = useState(() => {
    const initial = ['objectTypes'];
    if (localFilters?.edgeDurationFilters?.length > 0) initial.push('edgeDuration');
    if (localFilters?.edgeContainmentFilters?.edges?.length > 0) initial.push('edgeContainment');
    return initial;
  });

  // Also expand dynamically when filters are added while the panel is already mounted
  useEffect(() => {
    const toExpand = [];
    if (localFilters.edgeDurationFilters?.length > 0) toExpand.push('edgeDuration');
    if (localFilters.edgeContainmentFilters?.edges?.length > 0) toExpand.push('edgeContainment');
    if (toExpand.length > 0) {
      setExpandedSections(prev => [...new Set([...prev, ...toExpand])]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localFilters.edgeDurationFilters?.length, localFilters.edgeContainmentFilters?.edges?.length]);

  // Determine which filters to show
  const shouldShowFilter = (filterType) => {
    if (!enabledFilterTypes) return true;
    return enabledFilterTypes.includes(filterType);
  };

  const allSectionKeys = ['objectTypes', 'dateRange', 'eventAttributes', 'edgeDuration', 'edgeContainment', 'activityNodes', 'objectAttributes']
    .filter(shouldShowFilter);

  const handleExpandCollapseAll = () => {
    const allExpanded = allSectionKeys.every(k => expandedSections.includes(k));
    setExpandedSections(allExpanded ? [] : allSectionKeys);
  };

  const isAllExpanded = allSectionKeys.length > 0 && allSectionKeys.every(k => expandedSections.includes(k));

  const hasActiveFilters =
    (shouldShowFilter('objectTypes') && localFilters.objectTypes?.length > 0) ||
    (shouldShowFilter('dateRange') && localFilters.dateRange?.enabled) ||
    (shouldShowFilter('eventAttributes') && localFilters.eventAttributeFilters?.some(f => f.enabled)) ||
    (shouldShowFilter('edgeDuration') && localFilters.edgeDurationFilters?.length > 0) ||
    (shouldShowFilter('edgeContainment') && localFilters.edgeContainmentFilters?.enabled) ||
    (shouldShowFilter('activityNodes') && localFilters.activityNodeFilters?.enabled) ||
    (shouldShowFilter('objectAttributes') && localFilters.objectAttributeFilters?.some(f => f.enabled));

  const handleClearAll = () => {
    allSectionKeys.forEach(sectionKey => {
      const filterKey = FILTER_TYPE_TO_KEY[sectionKey];
      updateLocalFilter(filterKey, EMPTY_FILTER_VALUES[filterKey]);
    });
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-gray-700">Local Filters</h3>
        <div className="flex items-center gap-3">
          {hasActiveFilters && (
            <button
              type="button"
              onClick={handleClearAll}
              disabled={disabled}
              className="text-xs text-red-500 hover:text-red-700 disabled:opacity-50"
            >
              Clear All
            </button>
          )}
          <button
            type="button"
            onClick={handleExpandCollapseAll}
            className="text-xs text-blue-600 hover:text-blue-800"
          >
            {isAllExpanded ? 'Collapse All' : 'Expand All'}
          </button>
        </div>
      </div>

      <Accordion
        type="multiple"
        value={expandedSections}
        onValueChange={setExpandedSections}
        className="space-y-3"
      >
        {shouldShowFilter('objectTypes') && (
          <AccordionItem value="objectTypes">
            <AccordionTrigger>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-900">Object Types</span>
                {localFilters.objectTypes?.length > 0 && (
                  <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                    {localFilters.objectTypes.length}
                  </span>
                )}
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <ObjectTypeFilterControl
                value={localFilters.objectTypes}
                onChange={(value) => updateLocalFilter('objectTypes', value)}
                activeFile={activeFile}
                disabled={disabled}
              />
            </AccordionContent>
          </AccordionItem>
        )}

        {shouldShowFilter('dateRange') && (
          <AccordionItem value="dateRange">
            <AccordionTrigger>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-900">Date Range</span>
                {localFilters.dateRange?.enabled && (
                  <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded">1</span>
                )}
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <div className="space-y-3">
                <DateRangePresets
                  value={localFilters.dateRange}
                  onChange={(value) => updateLocalFilter('dateRange', value)}
                />
                <DateRangeFilterControl
                  value={localFilters.dateRange}
                  onChange={(value) => updateLocalFilter('dateRange', value)}
                  activeFile={activeFile}
                  disabled={disabled}
                  showTime={dateRangeShowTime}
                />
              </div>
            </AccordionContent>
          </AccordionItem>
        )}

        {shouldShowFilter('eventAttributes') && (
          <AccordionItem value="eventAttributes">
            <AccordionTrigger>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-900">Event Attributes</span>
                {localFilters.eventAttributeFilters?.filter(f => f.enabled).length > 0 && (
                  <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                    {localFilters.eventAttributeFilters.filter(f => f.enabled).length}
                  </span>
                )}
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <EventAttributeFilterControl
                value={localFilters.eventAttributeFilters}
                onChange={(value) => updateLocalFilter('eventAttributeFilters', value)}
                activeFile={activeFile}
                disabled={disabled}
              />
            </AccordionContent>
          </AccordionItem>
        )}

        {shouldShowFilter('edgeDuration') && (
          <AccordionItem value="edgeDuration">
            <AccordionTrigger>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-900">Edge Duration</span>
                {localFilters.edgeDurationFilters?.length > 0 && (
                  <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                    {localFilters.edgeDurationFilters.length}
                  </span>
                )}
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <EdgeDurationFilterControl
                value={localFilters.edgeDurationFilters}
                onChange={(value) => updateLocalFilter('edgeDurationFilters', value)}
                disabled={disabled}
                onAddEdge={onAddEdgeDuration}
              />
            </AccordionContent>
          </AccordionItem>
        )}

        {shouldShowFilter('edgeContainment') && (
          <AccordionItem value="edgeContainment">
            <AccordionTrigger>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-900">Edge Containment</span>
                {localFilters.edgeContainmentFilters?.enabled && (
                  <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded">1</span>
                )}
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <EdgeContainmentFilterControl
                value={localFilters.edgeContainmentFilters}
                onChange={(value) => updateLocalFilter('edgeContainmentFilters', value)}
                disabled={disabled}
                onAddEdge={onAddEdgeContainment}
              />
            </AccordionContent>
          </AccordionItem>
        )}

        {shouldShowFilter('activityNodes') && (
          <AccordionItem value="activityNodes">
            <AccordionTrigger>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-900">Activity Nodes</span>
                {localFilters.activityNodeFilters?.enabled && (
                  <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded">1</span>
                )}
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <ActivityNodeFilterControl
                value={localFilters.activityNodeFilters}
                onChange={(value) => updateLocalFilter('activityNodeFilters', value)}
                activeFile={activeFile}
                disabled={disabled}
              />
            </AccordionContent>
          </AccordionItem>
        )}

        {shouldShowFilter('objectAttributes') && (
          <AccordionItem value="objectAttributes">
            <AccordionTrigger>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-900">Object Attributes</span>
                {localFilters.objectAttributeFilters?.filter(f => f.enabled).length > 0 && (
                  <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                    {localFilters.objectAttributeFilters.filter(f => f.enabled).length}
                  </span>
                )}
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <ObjectAttributeFilterControl
                value={localFilters.objectAttributeFilters}
                onChange={(value) => updateLocalFilter('objectAttributeFilters', value)}
                activeFile={activeFile}
                disabled={disabled}
                showMode
              />
            </AccordionContent>
          </AccordionItem>
        )}
      </Accordion>
    </div>
  );
};

export default LocalFilterPanel;
